import Logger from "../Component/Logger";
import { getLoggerBaseURL } from "../utils/getLoggerBaseURL";

// Initialize Logger
const logger = new Logger(getLoggerBaseURL());

const originalFetch = window.fetch;

const primaryURL = process.env.REACT_APP_PRIMARY_BASE_URL;
const secondaryURL = process.env.REACT_APP_SECONDARY_BASE_URL;

const DEFAULT_TIMEOUT_MS = 30_000;
/** Excel / ERP uploads: DB save should finish within a few minutes; WhatsApp runs after response. */
const UPLOAD_TIMEOUT_MS = 15 * 60_000;
/** Deep order-list pages can take longer (large skip/sort). */
const LIST_TIMEOUT_MS = 90_000;

const isAbsoluteURL = (url) => /^https?:\/\//i.test(url);
const buildURL = (base, endpoint) => {
    if (!endpoint || typeof endpoint !== 'string') {
        throw new Error(`❌ Invalid endpoint in failoverFetch: ${endpoint}`);
    }
    if (!base) {
        throw new Error(`❌ Missing API base URL in failoverFetch`);
    }
    const cleanBase = base.replace(/\/+$/, '');
    const cleanPath = endpoint.replace(/^\/+/, '');
    return isAbsoluteURL(endpoint) ? endpoint : `${cleanBase}/${cleanPath}`;
};

const isUploadRequest = (path = '', options = {}) => {
    if (typeof FormData !== 'undefined' && options.body instanceof FormData) {
        return true;
    }
    return /\/(upload|ERP|upload-marg|uploadImages|oldbills\/upload)\b/i.test(path);
};

const isHeavyListRequest = (path = '', options = {}) => {
    const method = String(options.method || 'GET').toUpperCase();
    if (method !== 'GET') return false;
    // Paginated order lists (Current / Assigned / Completed / etc.)
    return /\/api\/ordera\/[^/]+(\?|$)/i.test(path) ||
        /\/api\/ordera\/(completed|incompleted|orderIncomplete|assign)\//i.test(path);
};

const resolveTimeout = (path = '', options = {}) => {
    if (isUploadRequest(path, options)) return UPLOAD_TIMEOUT_MS;
    if (isHeavyListRequest(path, options)) return LIST_TIMEOUT_MS;
    return DEFAULT_TIMEOUT_MS;
};

const fetchWithTimeout = (url, options = {}, timeout = DEFAULT_TIMEOUT_MS) => {
    return new Promise((resolve, reject) => {
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timer = setTimeout(() => {
            controller?.abort();
            reject(new Error(`Request timed out after ${Math.round(timeout / 1000)}s`));
        }, timeout);

        const fetchOptions = controller
            ? { ...options, signal: controller.signal }
            : options;

        originalFetch(url, fetchOptions)
            .then((res) => {
                clearTimeout(timer);
                resolve(res);
            })
            .catch((err) => {
                clearTimeout(timer);
                if (err?.name === 'AbortError') {
                    reject(new Error(`Request timed out after ${Math.round(timeout / 1000)}s`));
                    return;
                }
                reject(err);
            });
    });
};

export async function failoverFetch(input, init = {}) {
    const path = typeof input === 'string' ? input : input.url;

    if (isAbsoluteURL(path)) {
        return originalFetch(path, init); // use original fetch to avoid recursion
    }

    const timeout = resolveTimeout(path, init);
    // Don't re-POST large multipart bodies to a second host on timeout/local-dev miss.
    const allowSecondary =
        Boolean(secondaryURL) &&
        secondaryURL !== primaryURL &&
        !isUploadRequest(path, init);

    const tryPrimary = async () => {
        const fullURL = buildURL(primaryURL, path);

        try {
            const res = await fetchWithTimeout(fullURL, init, timeout);

            if (!res.ok) {
                console.warn(`[Failover] Primary server responded with status: ${res.status}`);
                return res;
            }

            return res;
        } catch (err) {
            logger.log(`[❌ Failover] PRIMARY server fetch failed`, "ERROR");
            logger.log(`URL: ${fullURL}`, "ERROR");
            logger.log(`Error name: ${err.name}`, "ERROR");
            logger.log(`Error message: ${err.message}`, "ERROR");
            throw err;
        }
    };

    const trySecondary = async () => {
        const fullURL = buildURL(secondaryURL, path);

        try {
            const res = await fetchWithTimeout(fullURL, init, timeout);
            return res;
        } catch (err) {
            logger.log(`[❌ Failover] SECONDARY server fetch failed`, "ERROR");
            logger.log(`URL: ${fullURL}`, "ERROR");
            logger.log(`Error: ${err.message}`, "ERROR");
            throw err;
        }
    };

    try {
        return await tryPrimary();
    } catch (primaryErr) {
        if (!allowSecondary) {
            throw primaryErr;
        }
        try {
            return await trySecondary();
        } catch (secondaryErr) {
            // Prefer the primary reason (e.g. timeout) over a dead secondary's "Failed to fetch"
            throw primaryErr || secondaryErr;
        }
    }
}

export const getServerFlags = () => {
    const flag = localStorage.getItem('usePrimaryServer') !== 'false';
    return {
        primary: flag,
        secondary: !flag
    };
};
